// @ts-nocheck
/**
 * Flavor preference picker — accordion 3-state UI
 * Writes to cart order note: "Prefix: Exclude: [...] | Extra: [...]"
 */

const DEFAULT_DEBOUNCE_MS = 500;
const DEFAULT_NOTE_PREFIX = 'EXALT LIFE PREFERENCES';

function cartUpdateUrl() {
  if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_update_url) {
    return Theme.routes.cart_update_url;
  }
  return '/cart/update.js';
}

document.addEventListener('alpine:init', () => {
  Alpine.data('flavorPreferencePicker', (options = {}) => ({

    // ── config ──────────────────────────────────────────────
    cartAttributeKey:
      typeof options.cartAttributeKey === 'string' && options.cartAttributeKey.trim()
        ? options.cartAttributeKey.trim()
        : DEFAULT_NOTE_PREFIX,
    debounceMs:
      typeof options.debounceMs === 'number' && Number.isFinite(options.debounceMs) && options.debounceMs >= 0
        ? options.debounceMs
        : DEFAULT_DEBOUNCE_MS,

    // ── state ───────────────────────────────────────────────
    categories: [],       // [{ name, flavors: [{ flavor, benefit, isVegan, hasCaffeine }] }]
    flavorStates: {},     // { [flavorName]: 'default' | 'exclude' | 'extra' }
    manualStates: {},     // { [flavorName]: true } — only states changed by customer
    hasPreference: null,
    activeDesc: null,
    openSections: [],     // category names currently expanded
    _debounceTimer: null,

    // ── init ────────────────────────────────────────────────
    init() {
      const el = this.$refs.flavorConfigJson;
      let rows = [];
      try {
        rows = JSON.parse(el ? el.textContent.trim() : '[]');
      } catch (e) {
        console.warn('[ctr-fpp] Could not parse flavor config JSON', e);
      }

      // Group into categories preserving order
      const catMap = new Map();
      for (const row of rows) {
        const cat = row.category || 'Other';
        if (!catMap.has(cat)) catMap.set(cat, []);
        catMap.get(cat).push({
          flavor: row.flavor,
          category: cat,
          benefit: row.benefit || '',
          isVegan: row.isVegan === true,
          hasCaffeine: row.hasCaffeine === true,
          description: row.description || null,
        });
        // Default state for every flavor
        if (!this.flavorStates[row.flavor]) {
          this.flavorStates[row.flavor] = 'default';
        }
      }

      this.categories = Array.from(catMap.entries()).map(([name, flavors]) => ({ name, flavors }));

      // Restore from existing cart note if present
      this._restoreFromNote();

      // Wire up variant sync
      this._initVariantSync();
    },

    // ── variant sync ─────────────────────────────────────────
    _getVariantPrefs() {
      try {
        const input = document.querySelector('input[name="id"]');
        if (!input) return null;
        const variants = meta?.product?.variants;
        if (!variants) return null;
        const variant = variants.find(v => String(v.id) === String(input.value));
        if (!variant) return null;
        // Use SKU if available, fall back to name/title
        const identifier = variant.sku || variant.name || variant.title || '';
        const isVeganOnly = (identifier.includes('Vegan') || identifier.includes('vegan')) &&
                            !identifier.includes('Whey') && !identifier.includes('Collagen') && !identifier.includes('Standard');
        const isCaffeineFree = identifier.includes('Caffeine Free') || identifier.includes('No-Caffeine');
        return { excludeNonVegan: isVeganOnly, excludeCaffeine: isCaffeineFree };
      } catch (e) {
        return null;
      }
    },

    _applyVariantPrefs() {
      const prefs = this._getVariantPrefs();
      if (!prefs) return;
      // Reset non-manual states to default, then re-apply variant prefs
      for (const key of Object.keys(this.flavorStates)) {
        if (!this.manualStates[key]) {
          this.flavorStates[key] = 'default';
        }
      }
      this.applyPreferences(prefs);
    },

    _initVariantSync() {
      // Apply on load
      this.$nextTick(() => this._applyVariantPrefs());

      // Poll for variant changes every 500ms
      let lastVariantId = null;
      setInterval(() => {
        const input = document.querySelector('input[name="id"]');
        if (!input) return;
        const currentId = input.value;
        if (currentId && currentId !== lastVariantId) {
          lastVariantId = currentId;
          this._applyVariantPrefs();
        }
      }, 500);
    },

    // ── yes/no gate ─────────────────────────────────────────
    setPreference(val) {
      this.hasPreference = val;
      if (!val) {
        // Reset all states and clear note when No selected
        for (const key of Object.keys(this.flavorStates)) {
          this.flavorStates[key] = 'default';
        }
        this._scheduleNoteSync();
      }
    },

    // ── accordion ───────────────────────────────────────────
    toggleSection(name) {
      if (this.openSections.includes(name)) {
        this.openSections = this.openSections.filter(n => n !== name);
      } else {
        this.openSections = [...this.openSections, name];
      }
    },

    sectionExcludeCount(catName) {
      const cat = this.categories.find(c => c.name === catName);
      if (!cat) return 0;
      return cat.flavors.filter(f => this.flavorStates[f.flavor] === 'exclude').length;
    },

    sectionExtraCount(catName) {
      const cat = this.categories.find(c => c.name === catName);
      if (!cat) return 0;
      return cat.flavors.filter(f => this.flavorStates[f.flavor] === 'extra').length;
    },

    // ── 3-state toggle ──────────────────────────────────────
    getState(flavor) {
      return this.flavorStates[flavor] || 'default';
    },

    setState(flavor, state) {
      // Toggle off if already active
      if (this.flavorStates[flavor] === state) {
        this.flavorStates[flavor] = 'default';
        this.manualStates[flavor] = true;
      } else {
        this.flavorStates[flavor] = state;
        this.manualStates[flavor] = true;
      }
      this._scheduleNoteSync();
    },

    // ── external API (for future variant-linking stage) ─────
    // Call this from outside to pre-seed states based on variant selection
    applyPreferences({ excludeNonVegan = false, excludeCaffeine = false } = {}) {
      for (const cat of this.categories) {
        for (const item of cat.flavors) {
          // Only auto-set if not manually changed by customer
          if (!this.manualStates[item.flavor]) {
            if ((excludeNonVegan && !item.isVegan) || (excludeCaffeine && item.hasCaffeine)) {
              this.flavorStates[item.flavor] = 'exclude';
            } else {
              this.flavorStates[item.flavor] = 'default';
            }
          }
        }
      }
      this._scheduleNoteSync();
    },

    // ── note helpers ────────────────────────────────────────
    _buildNoteString() {
      // Only write manually changed states — not auto-set by variant
      const catExclude = {};
      const catExtra = {};

      for (const cat of this.categories) {
        for (const item of cat.flavors) {
          if (!this.manualStates[item.flavor]) continue;
          const s = this.flavorStates[item.flavor];
          if (s === 'exclude') {
            if (!catExclude[cat.name]) catExclude[cat.name] = [];
            catExclude[cat.name].push(item.flavor);
          }
          if (s === 'extra') {
            if (!catExtra[cat.name]) catExtra[cat.name] = [];
            catExtra[cat.name].push(item.flavor);
          }
        }
      }

      const totalExclude = Object.values(catExclude).flat();
      const totalExtra = Object.values(catExtra).flat();
      if (totalExclude.length === 0 && totalExtra.length === 0) return '';

      const lines = [];
      const prefix = this.cartAttributeKey || 'EXALT LIFE PREFERENCES';
      lines.push(prefix);

      if (totalExclude.length) {
        lines.push('');
        lines.push('EXCLUDE:');
        this.categories
          .filter(cat => catExclude[cat.name])
          .forEach(cat => {
            lines.push(cat.name.toUpperCase() + ': ' + catExclude[cat.name].join(' · '));
          });
      }

      if (totalExtra.length) {
        lines.push('');
        lines.push('EXTRA:');
        this.categories
          .filter(cat => catExtra[cat.name])
          .forEach(cat => {
            lines.push(cat.name.toUpperCase() + ': ' + catExtra[cat.name].join(' · '));
          });
      }

      return lines.join('\n');
    },

    _scheduleNoteSync() {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this._syncNote(), this.debounceMs);
    },

    async _syncNote() {
      const note = this._buildNoteString();
      try {
        await fetch(cartUpdateUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ note }),
        });
      } catch (e) {
        console.warn('[ctr-fpp] Cart note sync failed', e);
      }
    },

    _restoreFromNote() {
      // Best-effort parse of existing note back into state
      // Format: "Prefix: Exclude: [a, b] | Extra: [c]"
      try {
        const note = document.querySelector('[data-cart-note]')?.value || '';
        if (!note) return;

        const body = this.cartAttributeKey
          ? note.replace(new RegExp('^' + this.cartAttributeKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ':\\s*'), '')
          : note;

        const parts = body.split('|').map(p => p.trim());
        for (const part of parts) {
          if (part.startsWith('Exclude:')) {
            const names = part.replace('Exclude:', '').split(',').map(s => s.trim()).filter(Boolean);
            for (const n of names) if (this.flavorStates[n] !== undefined) this.flavorStates[n] = 'exclude';
          }
          if (part.startsWith('Extra:')) {
            const names = part.replace('Extra:', '').split(',').map(s => s.trim()).filter(Boolean);
            for (const n of names) if (this.flavorStates[n] !== undefined) this.flavorStates[n] = 'extra';
          }
        }
      } catch (e) {
        // Silently ignore restore errors
      }
    },

  }));
});